import React, { createContext, useContext, useState, ReactNode } from 'react';
import { useUI } from './UIContext';

const API_URL = import.meta.env.PROD ? '/api/index.php' : (import.meta.env.VITE_API_URL || '/api/index.php');

interface AuthContextType {
    isAuthenticated: boolean;
    login: (password: string) => Promise<boolean>;
    changePassword: (oldPassword: string, newPassword: string) => Promise<boolean>;
    logout: () => void;
    token: string | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const { addToast } = useUI();

    const [isAuthenticated, setIsAuthenticated] = useState<boolean>(() => {
        try { return sessionStorage.getItem('greysoft_auth') === 'true'; } catch { return false; }
    });

    const [token, setToken] = useState<string | null>(() => {
        try { return sessionStorage.getItem('greysoft_token'); } catch { return null; }
    });

    const login = async (password: string) => {
        try {
            const res = await fetch(API_URL, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ action: 'login', password })
            });
            const data = await res.json();

            if (data.success && data.token) {
                setIsAuthenticated(true);
                setToken(data.token);
                try {
                    sessionStorage.setItem('greysoft_auth', 'true');
                    sessionStorage.setItem('greysoft_token', data.token);
                } catch { }
                addToast('Giriş başarılı', 'success');
                return true;
            } else {
                addToast('Hatalı şifre!', 'error');
                return false;
            }
        } catch (e) {
            console.error("Login Error", e);
            addToast('Bağlantı hatası', 'error');
            return false;
        }
    };

    const changePassword = async (oldPassword: string, newPassword: string) => {
        try {
            const res = await fetch(API_URL, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ action: 'change-password', oldPassword, newPassword })
            });
            const data = await res.json();

            if (data.success) {
                addToast('Şifre başarıyla değiştirildi', 'success');
                return true;
            } else {
                addToast(data.error || 'Şifre değiştirilemedi', 'error');
                return false;
            }
        } catch (e) {
            console.error("Change Password Error", e);
            addToast('Bağlantı hatası', 'error');
            return false;
        }
    };

    const logout = () => {
        setIsAuthenticated(false);
        setToken(null);
        try {
            sessionStorage.removeItem('greysoft_auth');
            sessionStorage.removeItem('greysoft_token');
        } catch { }
        addToast('Çıkış yapıldı', 'info');
    };

    return (
        <AuthContext.Provider value={{ isAuthenticated, login, changePassword, logout, token }}>
            {children}
        </AuthContext.Provider>
    );
};
